// Quick Blog importer — turns pasted Notion / ChatGPT / Word text into CMS blocks.
// Entirely local: no AI, no credits, no network calls.

const rid = () => `blk_${Math.random().toString(36).slice(2, 10)}`;

const META_KEYS = {
    title: "title", slug: "slug", excerpt: "excerpt", summary: "excerpt",
    "seo title": "seo_title", "meta title": "seo_title",
    "meta description": "meta_description", "seo description": "meta_description",
    "focus keyword": "focus_keyword", keyword: "focus_keyword", "primary keyword": "focus_keyword",
    category: "category", tags: "tags", author: "author", "published by": "author",
    "featured image": "featured_image", "cover image": "featured_image",
    "featured image alt": "featured_image_alt", "image alt": "featured_image_alt",
    style: "style", "reviewed by": "reviewed_by", "review date": "review_date",
    products: "products", product: "products", consultation: "consultation",
    canonical: "canonical_url", "canonical url": "canonical_url",
};

const STOP = new Set(["the", "a", "an", "and", "or", "for", "with", "that", "this", "from", "your", "you",
    "are", "was", "how", "why", "what", "ways", "way", "tips", "guide", "best", "top", "natural", "to",
    "of", "in", "on", "at", "is", "it", "can", "do", "does", "about", "support", "supporting"]);

export const slugify = (v = "") =>
    v.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").replace(/-{2,}/g, "-");

const escapeHtml = (s = "") => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const inlineToHtml = (raw = "") => {
    let s = escapeHtml(raw.trim());
    s = s.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, "");
    s = s.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, '<a href="$2">$1</a>');
    s = s.replace(/\*\*\*([^*]+)\*\*\*/g, "<strong><em>$1</em></strong>");
    s = s.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    s = s.replace(/(^|[\s(])\*([^*\n]+)\*(?=[\s.,;:!?)]|$)/g, "$1<em>$2</em>");
    s = s.replace(/(^|[\s(])_([^_\n]+)_(?=[\s.,;:!?)]|$)/g, "$1<em>$2</em>");
    s = s.replace(/`([^`]+)`/g, "<strong>$1</strong>");
    return s;
};

const stripInline = (raw = "") => inlineToHtml(raw).replace(/<[^>]+>/g, "").trim();

// ---------------------------------------------------------------- HTML paste (Notion, Docs, Word)
export function htmlToMarkdown(html = "") {
    const doc = new DOMParser().parseFromString(html, "text/html");
    doc.querySelectorAll("script, style, noscript").forEach((n) => n.remove());
    const out = [];

    const inline = (node) => {
        let s = "";
        node.childNodes.forEach((c) => {
            if (c.nodeType === 3) { s += c.textContent; return; }
            if (c.nodeType !== 1) return;
            const tag = c.tagName.toLowerCase();
            const inner = inline(c).trim();
            if (!inner && tag !== "br") return;
            if (tag === "strong" || tag === "b") s += `**${inner}**`;
            else if (tag === "em" || tag === "i") s += `*${inner}*`;
            else if (tag === "a") s += `[${inner}](${c.getAttribute("href") || "#"})`;
            else if (tag === "br") s += "\n";
            else s += inner;
        });
        return s.replace(/[ \t]+/g, " ");
    };

    const walk = (node) => {
        node.childNodes.forEach((el) => {
            if (el.nodeType === 3) {
                const t = el.textContent.trim();
                if (t) out.push(t, "");
                return;
            }
            if (el.nodeType !== 1) return;
            const tag = el.tagName.toLowerCase();
            switch (tag) {
                case "h1": out.push(`# ${inline(el).trim()}`, ""); break;
                case "h2": out.push(`## ${inline(el).trim()}`, ""); break;
                case "h3": case "h4": case "h5": out.push(`### ${inline(el).trim()}`, ""); break;
                case "p": {
                    const t = inline(el).trim();
                    if (t) out.push(t, "");
                    break;
                }
                case "ul": case "ol":
                    el.querySelectorAll(":scope > li").forEach((li, i) => {
                        out.push(tag === "ul" ? `- ${inline(li).trim()}` : `${i + 1}. ${inline(li).trim()}`);
                    });
                    out.push("");
                    break;
                case "blockquote": out.push(`> ${inline(el).trim()}`, ""); break;
                case "hr": out.push("---", ""); break;
                case "img": {
                    const src = el.getAttribute("src") || "";
                    if (src && !src.startsWith("data:")) out.push(`![${el.getAttribute("alt") || ""}](${src})`, "");
                    break;
                }
                case "figure": {
                    const img = el.querySelector("img");
                    const cap = el.querySelector("figcaption");
                    if (img?.getAttribute("src")) out.push(`![${img.getAttribute("alt") || ""}](${img.getAttribute("src")})`);
                    if (cap) out.push(`Caption: ${cap.textContent.trim()}`);
                    out.push("");
                    break;
                }
                case "table": {
                    const rows = [...el.querySelectorAll("tr")];
                    rows.forEach((tr, ri) => {
                        const cells = [...tr.children].map((td) => inline(td).trim());
                        out.push(`| ${cells.join(" | ")} |`);
                        if (ri === 0) out.push(`| ${cells.map(() => "---").join(" | ")} |`);
                    });
                    out.push("");
                    break;
                }
                case "br": break;
                default: walk(el);
            }
        });
    };

    walk(doc.body);
    return out.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

// ---------------------------------------------------------------- meta block
function readMeta(lines) {
    const meta = {};
    let cut = 0;
    let sawDivider = false;
    for (let i = 0; i < Math.min(lines.length, 40); i++) {
        const line = lines[i].trim();
        if (!line) { cut = i + 1; continue; }
        if (/^-{3,}$/.test(line)) { cut = i + 1; sawDivider = true; break; }
        const m = line.match(/^\*{0,2}([A-Za-z][A-Za-z ]{1,24}?)\*{0,2}\s*:\s*(.*)$/);
        const key = m && META_KEYS[m[1].trim().toLowerCase()];
        if (!key) {
            if (Object.keys(meta).length === 0) return { meta: {}, body: lines };
            break;
        }
        meta[key] = stripInline(m[2]);
        cut = i + 1;
    }
    if (!Object.keys(meta).length) return { meta: {}, body: lines };
    return { meta, body: lines.slice(cut), sawDivider };
}

// ---------------------------------------------------------------- body → blocks
function bodyToBlocks(lines) {
    const blocks = [];
    const warnings = [];
    let para = [];
    let quote = [];
    let list = null;
    let table = null;
    let callout = null;
    let faq = null;

    const flushPara = () => {
        if (!para.length) return;
        const html = para.map((l) => `<p>${inlineToHtml(l)}</p>`).join("");
        if (callout) callout.html += html;
        else blocks.push({ id: rid(), type: "text", html });
        para = [];
    };
    const flushQuote = () => {
        if (!quote.length) return;
        blocks.push({ id: rid(), type: "quote", html: `<p>${inlineToHtml(quote.join(" "))}</p>`, cite: "" });
        quote = [];
    };
    const flushList = () => {
        if (!list) return;
        const tag = list.ordered ? "ol" : "ul";
        const html = `<${tag}>${list.items.map((i) => `<li>${inlineToHtml(i)}</li>`).join("")}</${tag}>`;
        if (callout) callout.html += html;
        else blocks.push({ id: rid(), type: "text", html });
        list = null;
    };
    const flushTable = () => {
        if (!table) return;
        blocks.push({ id: rid(), type: "table", columns: table.columns, rows: table.rows });
        table = null;
    };
    const flushCallout = () => {
        if (!callout) return;
        blocks.push({ id: rid(), type: callout.kind, title: callout.title, html: callout.html });
        callout = null;
    };
    const flushFaq = () => {
        if (!faq) return;
        const items = faq.items.filter((i) => i.question);
        if (items.length) blocks.push({ id: rid(), type: "faq", title: faq.title, items });
        faq = null;
    };
    const flushSoft = () => { flushPara(); flushQuote(); flushList(); flushTable(); };
    const flushAll = () => { flushSoft(); flushCallout(); flushFaq(); };

    for (let raw of lines) {
        const line = (raw || "").replace(/\s+$/, "");
        const t = line.trim();

        if (!t) { flushSoft(); continue; }

        // shortcodes
        const close = t.match(/^\[\[\/(tip|info|warning)\]\]$/i);
        if (close) { flushPara(); flushList(); flushCallout(); continue; }
        const open = t.match(/^\[\[(tip|info|warning)(?::\s*([^\]]+))?\]\]\s*(.*)$/i);
        if (open) {
            flushAll();
            callout = { kind: open[1].toLowerCase(), title: (open[2] || "").trim(), html: "" };
            if (open[3]?.trim()) { callout.html += `<p>${inlineToHtml(open[3])}</p>`; }
            continue;
        }
        const product = t.match(/^\[\[product:\s*([^\]]+)\]\]$/i);
        if (product) { flushAll(); blocks.push({ id: rid(), type: "product", slug: slugify(product[1]) }); continue; }
        const consult = t.match(/^\[\[consult(?:ation)?(?::\s*([^\]]*))?\]\]$/i);
        if (consult) {
            flushAll();
            blocks.push({ id: rid(), type: "consultation", title: "Need personalised guidance?",
                body: "Get a consultation based on your individual concerns.",
                label: "Choose your consultation", area: (consult[1] || "").trim().toLowerCase().replace(/^general$/, "") });
            continue;
        }

        // FAQ pairs
        if (faq) {
            const q = t.match(/^\*{0,2}(?:Q|Question)\s*[:.)-]\s*\*{0,2}\s*(.+)$/i);
            const a = t.match(/^\*{0,2}(?:A|Answer)\s*[:.)-]\s*\*{0,2}\s*(.+)$/i);
            if (q) { faq.items.push({ question: stripInline(q[1]), answer: "" }); continue; }
            if (a) {
                if (!faq.items.length) faq.items.push({ question: "", answer: "" });
                const item = faq.items[faq.items.length - 1];
                item.answer = `<p>${inlineToHtml(a[1])}</p>`;
                continue;
            }
            if (/^#{1,4}\s+/.test(t)) { /* fall through to heading, closes the FAQ */ }
            else if (faq.items.length) {
                const item = faq.items[faq.items.length - 1];
                item.answer += `<p>${inlineToHtml(t)}</p>`;
                continue;
            }
        }

        // heading
        const h = t.match(/^(#{1,4})\s+(.*)$/);
        if (h) {
            flushAll();
            const level = Math.min(Math.max(h[1].length, 2), 4);
            const text = stripInline(h[2]);
            if (/^(faq|frequently asked questions?|common questions)$/i.test(text)) {
                faq = { title: text.length > 4 ? text : "Frequently asked questions", items: [] };
                continue;
            }
            blocks.push({ id: rid(), type: "heading", level, text });
            continue;
        }

        // divider
        if (/^([-*_]\s?){3,}$/.test(t)) { flushAll(); blocks.push({ id: rid(), type: "divider" }); continue; }

        // image
        const img = t.match(/^!\[([^\]]*)\]\(([^)\s]+)\)$/);
        const bare = t.match(/^(https?:\/\/\S+\.(?:png|jpe?g|webp|gif|avif)(?:\?\S*)?)$/i);
        if (img || bare) {
            flushAll();
            blocks.push({ id: rid(), type: "image", url: img ? img[2] : bare[1], alt: img ? stripInline(img[1]) : "",
                caption: "", align: "center", width: "full", link: "", new_tab: false });
            continue;
        }

        // caption for the previous image
        const cap = t.match(/^caption\s*:\s*(.+)$/i);
        if (cap && blocks.length && blocks[blocks.length - 1].type === "image") {
            blocks[blocks.length - 1].caption = stripInline(cap[1]);
            continue;
        }

        // table row
        if (/^\|.*\|$/.test(t)) {
            flushPara(); flushQuote(); flushList();
            const cells = t.slice(1, -1).split("|").map((c) => stripInline(c));
            if (cells.every((c) => /^:?-{2,}:?$/.test(c.trim()))) continue;
            if (!table) table = { columns: cells, rows: [] };
            else table.rows.push(cells);
            continue;
        }
        flushTable();

        // quote
        if (/^>\s?/.test(t)) { flushPara(); flushList(); quote.push(t.replace(/^>\s?/, "")); continue; }
        flushQuote();

        // lists
        const bullet = t.match(/^[-*•]\s+(.*)$/);
        const numbered = t.match(/^\d+[.)]\s+(.*)$/);
        if (bullet || numbered) {
            flushPara();
            const ordered = !!numbered;
            if (!list || list.ordered !== ordered) { flushList(); list = { ordered, items: [] }; }
            list.items.push((bullet ? bullet[1] : numbered[1]).trim());
            continue;
        }
        flushList();

        para.push(t);
    }
    flushAll();

    if (!blocks.length) warnings.push("Nothing recognisable was found in the pasted text.");
    return { blocks, warnings };
}

// ---------------------------------------------------------------- suggestions (local, free)
const trimTo = (s = "", max) => {
    if (s.length <= max) return s;
    const cut = s.slice(0, max);
    return `${cut.slice(0, cut.lastIndexOf(" ") > 40 ? cut.lastIndexOf(" ") : max).trim()}…`;
};

export function suggestSeo({ title = "", excerpt = "", blocks = [] }) {
    const firstText = (blocks.find((b) => b.type === "text")?.html || "").replace(/<[^>]+>/g, " ")
        .replace(/\s+/g, " ").trim();
    const base = excerpt || firstText;
    const words = title.toLowerCase().replace(/[^a-z0-9\s]/g, " ").split(/\s+/)
        .filter((w) => w.length > 2 && !STOP.has(w));
    const keyword = words.slice(0, 3).join(" ");
    const withBrand = `${title} | ReHealthMe`;
    return {
        seo_title: title.length >= 30 && withBrand.length <= 62 ? withBrand : trimTo(title, 62),
        meta_description: base ? trimTo(base, 155) : "",
        focus_keyword: keyword,
        excerpt: excerpt || trimTo(firstText, 160),
    };
}

// ---------------------------------------------------------------- main entry
export function parsePaste({ text = "", html = "" }) {
    const source = html && /<\/?(p|h[1-6]|ul|ol|li|div|table|img|blockquote)\b/i.test(html)
        ? htmlToMarkdown(html) : text;
    const lines = source.replace(/\r/g, "").replace(/\u00a0/g, " ").split("\n");
    const { meta, body } = readMeta(lines);
    const parsed = bodyToBlocks(body);
    const blocks = parsed.blocks;
    const warnings = [...parsed.warnings];

    let title = meta.title || "";
    if (!title) {
        const idx = blocks.findIndex((b) => (b.type === "heading" && b.text) || (b.type === "text" && b.html));
        if (idx >= 0) {
            const b = blocks[idx];
            title = b.type === "heading" ? b.text : (b.html.replace(/<[^>]+>/g, " ").trim().split(/(?<=[.!?])\s/)[0] || "");
            if (b.type === "heading" || title.length <= 90) blocks.splice(idx, 1);
        }
        if (title) warnings.push("No “Title:” line found — I used the first line of the paste.");
    }

    let excerpt = meta.excerpt || "";
    if (!excerpt) {
        const idx = blocks.findIndex((b) => b.type === "text" && b.html);
        if (idx === 0) {
            const plain = blocks[0].html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
            if (plain.length < 320) { excerpt = plain; blocks.splice(0, 1); }
        }
    }

    const suggested = suggestSeo({ title, excerpt, blocks });

    return {
        blocks,
        warnings,
        meta: {
            title: title.trim(),
            slug: slugify(meta.slug || title),
            excerpt: (excerpt || suggested.excerpt || "").trim(),
            seo_title: meta.seo_title || suggested.seo_title,
            meta_description: meta.meta_description || suggested.meta_description,
            focus_keyword: meta.focus_keyword || suggested.focus_keyword,
            canonical_url: meta.canonical_url || "",
            category_name: meta.category || "",
            author_name: meta.author || "",
            tags: (meta.tags || "").split(/[,;]/).map((t) => t.trim()).filter(Boolean),
            style: ["standard", "editorial", "medical_guide", "list_tips", "product_education"]
                .includes((meta.style || "").toLowerCase()) ? meta.style.toLowerCase() : "standard",
            featured_image: { url: meta.featured_image || "", alt: meta.featured_image_alt || "", caption: "" },
            product_slugs: (meta.products || "").split(/[,;]/).map((s) => slugify(s)).filter(Boolean),
            consultation_area: (meta.consultation || "").toLowerCase().replace(/^general$/, ""),
            reviewed_by: meta.reviewed_by || "",
            review_date: meta.review_date || "",
        },
        suggested,
    };
}

export const blockLabel = (b) => {
    switch (b.type) {
        case "heading": return `H${b.level || 2} — ${b.text}`;
        case "text": {
            const plain = (b.html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
            return `${/<[uo]l>/.test(b.html || "") ? "List" : "Paragraph"} — ${plain.slice(0, 60)}${plain.length > 60 ? "…" : ""}`;
        }
        case "image": return `Image — ${b.alt || b.url.split("/").pop()}`;
        case "quote": return "Quote";
        case "tip": case "info": case "warning": return `${b.type === "tip" ? "Tip" : b.type === "info" ? "Info" : "Warning"} box — ${b.title || ""}`;
        case "table": return `Table — ${(b.columns || []).join(" / ")}`;
        case "faq": return `FAQ — ${(b.items || []).length} question(s)`;
        case "product": return `Product card — ${b.slug}`;
        case "consultation": return "Consultation CTA";
        case "divider": return "Divider";
        default: return b.type;
    }
};
